import * as z from "zod";
import type { TFunction } from "i18next";
import { isValidCnpj } from "@shared/utils";

import type {
  CreateComplexRequest,
  UpdateComplexRequest,
  GetComplexByIdResponse,
} from "../api/complexes/complexes.types";

const phoneRegex = /^\(\d{2}\) \d{4,5}-\d{4}$/;

export const createComplexSchema = (t: TFunction) =>
  z.object({
    cnpj: z
      .string()
      .min(1, t("management.complexes.validation.cnpjRequired"))
      .regex(
        /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/,
        t("management.complexes.validation.cnpjInvalid"),
      )
      .refine((val) => isValidCnpj(val), {
        message: t("management.complexes.validation.cnpjInvalid"),
      }),
    name: z
      .string()
      .min(1, t("management.complexes.validation.companyRequired")),
    fantasyName: z
      .string()
      .min(1, t("management.complexes.validation.tradeNameRequired")),
    phone: z
      .string()
      .optional()
      .refine((val) => !val || phoneRegex.test(val), {
        message: t("management.complexes.validation.telefoneInvalid"),
      }),
    municipalRegistration: z.string().optional(),
    logo: z.any().optional(),

    address: z.object({
      zipCode: z
        .string()
        .optional()
        .refine((val) => !val || /^\d{5}-\d{3}$/.test(val), {
          message: t("management.complexes.validation.zipCodeInvalid"),
        }),
      street: z.string().optional(),
      complement: z.string().optional(),
      neighborhood: z.string().optional(),
      number: z
        .string()
        .optional()
        .refine(
          (val) => {
            if (!val?.trim()) {
              return true;
            }
            const parsed = Number(val.trim());
            return Number.isInteger(parsed) && parsed >= 1;
          },
          { message: t("management.complexes.validation.numberInvalid") },
        ),
      city: z.string().optional(),
      state: z.string().optional(),
      country: z.string().optional(),
    }),

    responsible: z
      .object({
        name: z.string().optional(),
        email: z
          .string()
          .optional()
          .refine((val) => !val || z.string().email().safeParse(val).success, {
            message: t("management.complexes.validation.emailInvalid"),
          }),
        phone: z
          .string()
          .optional()
          .refine((val) => !val || phoneRegex.test(val), {
            message: t("management.complexes.validation.telefoneInvalid"),
          }),
      })
      .optional(),
  });

export type ComplexFormData = z.infer<ReturnType<typeof createComplexSchema>>;

export const uploadLogoSchema = () =>
  z.object({
    logo: z.any().optional(),
  });

export const mapFormDataToCreateRequest = (
  data: ComplexFormData,
): CreateComplexRequest => ({
  cnpj: data.cnpj,
  name: data.name,
  fantasyName: data.fantasyName,
  phone: data.phone || undefined,
  municipalRegistration: data.municipalRegistration || undefined,
  address: {
    zipCode: data.address?.zipCode || undefined,
    street: data.address?.street || undefined,
    complement: data.address?.complement || undefined,
    number: data.address?.number?.trim()
      ? Number(data.address.number.trim())
      : undefined,
    neighborhood: data.address?.neighborhood || undefined,
    city: data.address?.city || undefined,
    state: data.address?.state || undefined,
    country: data.address?.country || undefined,
  },
  responsible: {
    name: data.responsible?.name || undefined,
    email: data.responsible?.email || undefined,
    phone: data.responsible?.phone || undefined,
  },
});

export const mapFormDataToUpdateRequest = (
  data: ComplexFormData,
): UpdateComplexRequest => mapFormDataToCreateRequest(data);

export const mapComplexResponseToFormData = (
  response: GetComplexByIdResponse,
): ComplexFormData => ({
  cnpj: response.cnpj ?? "",
  name: response.name ?? "",
  fantasyName: response.fantasyName ?? "",
  phone: response.phone ?? "",
  municipalRegistration: response.municipalRegistration ?? "",
  logo: response.logo ?? undefined,
  address: {
    zipCode: response.address?.zipCode ?? "",
    street: response.address?.street ?? "",
    complement: response.address?.complement ?? "",
    neighborhood: response.address?.neighborhood ?? "",
    number: response.address?.number?.toString() ?? "",
    city: response.address?.city ?? "",
    state: response.address?.state ?? "",
    country: response.address?.country ?? "",
  },
  responsible: {
    name: response.responsible?.name ?? "",
    email: response.responsible?.email ?? "",
    phone: response.responsible?.phone ?? "",
  },
});
